import React, { useState, useCallback, ChangeEvent, useRef, useEffect } from 'react';

import { translate } from '@vimeo/i18n-runtime';

import { useSearchDispatch, useSearchState } from '../SearchContext';
import {
  Input,
  InputWrapper,
  OuterWrapper,
  CancelIcon,
  SearchIcon,
  Button,
} from './SearchFieldStyled';

type Props = {
  onChange: (value: string) => void;
  closeSearch: () => void;
  setShowSearchBar?: (bool: boolean) => void;
  autofocus: boolean;
};
export default function SearchField({ onChange, closeSearch, setShowSearchBar, autofocus }: Props) {
  const { searchText, isSearching } = useSearchState();
  const searchDispatch = useSearchDispatch();
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.currentTarget;
      onChange(value);
      searchDispatch({ type: 'SEARCH_TEXT', payload: value });
    },
    [onChange, isSearching],
  );

  function handleFocus() {
    setFocused(true);
  }

  function handleBlur() {
    setShowSearchBar && !isSearching && setShowSearchBar(false);
    setFocused(false);
  }

  function onSearchClose() {
    setShowSearchBar && setShowSearchBar(false);
    searchDispatch({ type: 'SEARCH_TEXT', payload: '' });
    closeSearch();
  }

  useEffect(() => {
    if (!inputRef.current) {
      return;
    }

    if (autofocus) {
      inputRef.current.focus();
    }
  }, [inputRef]);

  return (
    <OuterWrapper>
      <InputWrapper focused={focused}>
        <Input
          aria-label={translate('Search')}
          ref={inputRef}
          value={searchText}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
        <Button onClick={isSearching ? onSearchClose : undefined} aria-label={translate('Search')}>
          {isSearching ? <CancelIcon /> : <SearchIcon />}
        </Button>
      </InputWrapper>
    </OuterWrapper>
  );
}
